import React, { useCallback, useEffect, useState } from 'react';

export type PageJumpPaginationProps = {
  /** 1-based current page */
  currentPage: number;
  /** Total number of pages (will be treated as at least 1) */
  pageCount: number;
  /** Called with the new 1-based page */
  onPageChange: (page: number) => void;
  /** When set and > 0, shows a small “N results” line below the controls */
  totalCount?: number;
  showResultCount?: boolean;
  className?: string;
  /** Prefix for input id (unique when multiple paginators exist on one page) */
  idPrefix?: string;
};

/**
 * Page jump field + “of N” + Prev/Next + optional result count.
 * Used on Calls; import explicitly as `PageJumpPagination` when adopting elsewhere.
 */
const PageJumpPagination: React.FC<PageJumpPaginationProps> = ({
  currentPage,
  pageCount: pageCountProp,
  onPageChange,
  totalCount,
  showResultCount = true,
  className = '',
  idPrefix = 'pagination',
}) => {
  const pageCount = Math.max(1, pageCountProp);
  const safePage = Math.min(Math.max(1, currentPage), pageCount);
  const canPrev = safePage > 1;
  const canNext = safePage < pageCount;

  const [pageInput, setPageInput] = useState(String(safePage));
  useEffect(() => {
    setPageInput(String(safePage));
  }, [safePage]);

  const commitPageJump = useCallback(() => {
    const raw = pageInput.trim();
    if (raw === '') {
      setPageInput(String(safePage));
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) {
      setPageInput(String(safePage));
      return;
    }
    const clamped = Math.min(Math.max(1, n), pageCount);
    onPageChange(clamped);
    setPageInput(String(clamped));
  }, [pageInput, pageCount, safePage, onPageChange]);

  const inputId = `${idPrefix}-page-jump`;

  return (
    <div className={`flex w-full flex-col items-stretch gap-1 sm:w-auto sm:items-center ${className}`.trim()}>
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
        <label htmlFor={inputId} className="sr-only">
          Jump to page number
        </label>
        <input
          id={inputId}
          type="number"
          min={1}
          max={pageCount}
          step={1}
          inputMode="numeric"
          aria-label="Page number"
          value={pageInput}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '' || /^\d+$/.test(v)) setPageInput(v);
          }}
          onBlur={commitPageJump}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
          className="min-h-11 w-[5.25rem] min-w-[4.5rem] rounded-md border border-gray-300 bg-white px-2 py-2 text-center text-sm font-medium tabular-nums text-gray-900 shadow-sm [appearance:textfield] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <span className="text-sm tabular-nums text-neutral-700">of {pageCount}</span>
        <button
          type="button"
          aria-label="Previous page"
          disabled={!canPrev}
          onClick={() => canPrev && onPageChange(safePage - 1)}
          className="min-h-11 min-w-11 shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Prev
        </button>
        <button
          type="button"
          aria-label="Next page"
          disabled={!canNext}
          onClick={() => canNext && onPageChange(safePage + 1)}
          className="min-h-11 min-w-11 shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
      {showResultCount && totalCount != null && totalCount > 0 && (
        <p className="text-center text-xs text-neutral-500">
          {totalCount.toLocaleString()} result{totalCount === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
};

export default PageJumpPagination;
